import { Component, OnInit, Input, Output, OnDestroy, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { EventEmitter } from '@angular/core';

import { cloneDeep, differenceBy } from 'lodash';
import { MessageService } from 'primeng/api';
import { catchError, filter, of, Subscription} from 'rxjs';

import { MediaSummaryModel } from '../../models/content-library-media-summary.model';
import { MediaModel } from '../../../../Common/ContentLibrary/models/content-library-media.model';
import { UserNoteModel } from '../../../../Common/Misc/models/user-note.model';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { NotesChange, UserNotesComponent } from '../../../../Common/TargetingShared/components/UserNotes/user-notes.component';
import { ContentLibraryMediaService } from '../../services/content-library-media.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ResourceActionTypes } from '../../../../Common/Basic/enums/resource-actions.enum';
import { copy } from '../../../../Common/Misc/helpers/copy/copy.helper';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { download } from '../../../../Common/Misc/helpers/download/download.helper';
import { formatFileSize } from '../../../../Common/Misc/helpers/formatting/media.helper';
import { resolveFileIcon } from '../../../Misc/helpers/file/fileIcon.helper';
import { UsersService } from '../../../../Core/Users/services/users.service';
@Component({
    selector: 'agpr-library-item-details',
    templateUrl: './library-item-details.html',
    styleUrls: ['./library-item-details.scss'],
    standalone: false
})

export class LibraryItemDetailsComponent implements OnInit, OnDestroy {

    readonly buttonTypes = ButtonTypesEnum;
    readonly formatFileSize = formatFileSize;
    readonly messageSeverityEnum = messageSeverityEnum;

    subscriptions: Subscription[] = [];
    editingMediaId: string;
    editedMediaName: string = '';
    thumbnailDimensions: string;
    preferences: PreferencesModel;
    dateFormat: string;
    hasError = false;
    isLoadingNames = false;
    currentItemId: string;
    private approverNames: string[] = [];

    @Input() currentlySelectedItems: MediaSummaryModel[] = [];
    @Input() selectedItem: MediaModel;
    @Input() currentUser: UserModel;
    @Input() set userNotes(notes: UserNoteModel[]) {
        if (!notes) {
            return;
        }
        this._userNotes = notes;
        this.userNotesCloned = cloneDeep(notes);
    }
    _userNotes: UserNoteModel[] = [];
    userNotesCloned: UserNoteModel[] = [];
    @Output() notesChange = new EventEmitter<UserNoteModel>();
    @Output() itemSelected: EventEmitter<MediaSummaryModel> = new EventEmitter<MediaSummaryModel>();

    @ViewChild('thumbnail') thumbnailImage: ElementRef;
    @ViewChild('userNotes') userNotesComponent: UserNotesComponent;
    @ViewChild('actionButtons') actionButtons: TemplateRef<any>;

    get fileType(): string {
        return this.selectedItem?.mimeType ? `${this.selectedItem.mimeType.split('/').pop()} File` : '';
    }

    constructor(
        private contentLibraryMediaService: ContentLibraryMediaService,
        private messageService: MessageService,
        private currentUserService: CurrentUserStateService,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
        private usersService: UsersService
    ) {
        this.subscriptions.push(
            this.currentUserService.getUser().subscribe(x => {
                this.currentUser = x;
            }),
        );
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.preferences = prefs;
                this.dateFormat = prefs.getDateFormatString(true, true, true);
            }),
        );
    }

    getImageDimensions(event: Event): void {
        const img = event.target as HTMLImageElement;
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        this.thumbnailDimensions = `${width} x ${height}`;
    }

    getFileIcon(mimeType: string): string {
        return resolveFileIcon(mimeType);
    }

    addNote(): void {
        this.userNotesComponent?.addNote();
    }

    copyUrl(event: Event, url: string): void {
        if (!url) {
            return;
        }
        copy.text(url);
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Briefings.reservedUrl.noUrlAvailable.message.summary'),
            detail: this.localizationService.transform('Briefings.reservedUrl.noUrlAvailable.message.detail'),
        });
        (event.target as HTMLElement)?.focus();
    }

    modifyNotes(notesChange: NotesChange) {
        let actionCall;
        const noteEdited = differenceBy(notesChange.notes, this.userNotesCloned, 'value');

        switch (notesChange.type) {
            case ResourceActionTypes.ADD_NOTE:
                actionCall = this.contentLibraryMediaService.createNote(noteEdited?.[0], this.selectedItem.id);
                break;
            case ResourceActionTypes.EDIT_NOTE:
                actionCall = this.contentLibraryMediaService.updateNote(noteEdited?.[0], this.selectedItem.id);
                break;
            case ResourceActionTypes.DELETE_NOTE:
                const noteToDeleteId = this._userNotes.find(usernote =>
                    !notesChange.notes.some(notes => notes.id === usernote.id))?.id;
                actionCall = this.contentLibraryMediaService.deleteNote(this.selectedItem.id, noteToDeleteId);
                break;
        }

        actionCall?.pipe(
            catchError(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.libraryMoveDialog.upload.error.label',
                        { type: notesChange.type })
                });
                return of('err');
            })
        ).subscribe(res => {
            if (res !== 'err') {
                if (res?.id && noteEdited.length) {
                    noteEdited[0].id = res.id;
                }
                this._userNotes = notesChange.notes;
                this.userNotesCloned = cloneDeep(this._userNotes);
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform(
                        'Common.lhp.libraryMoveDialog.upload.success.message',
                        { type: notesChange.type.split('_').join(' ').toLowerCase() }
                    )
                });
            }
        });
    }

    editMedia(media: MediaModel): void {
        this.editingMediaId = media.id?.toString();
        this.editedMediaName = media.name;
    }

    downloadMedia(item: MediaModel, event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.contentLibraryMediaService.getMediaModel(item.id).subscribe({
            next: (media) => {
                const extension = media.name.split('.').pop() || '';
                const fileName = media.name + (extension ? '' : '.jpg');

                fetch(media.url)
                    .then(response => {
                        if (!response.ok) { // response.ok is false for 4xx and 5xx status codes
                            if (response.status === 404) {
                                throw new Error('Resource not found (404)');
                            } else {
                                throw new Error(`HTTP error! status: ${response.status}`);
                            }
                        }
                        return response.blob();
                    })
                    .then(blob => {
                        download.blob(blob, fileName);
                    })
                    .catch(error => {
                        console.error('Download failed:', error);
                    });
            },
            error: (error) => {
                console.error('Failed to get media summary:', error);
            }
        });
    }

    saveMedia(media: MediaModel): void {
        if (!this.editedMediaName?.trim()) {
            this.editedMediaName = media.name;
            this.editingMediaId = null;
            return;
        }
        media.name = this.editedMediaName;
        this.contentLibraryMediaService.updateResource(media).subscribe(x => {
            this.editingMediaId = null;
        });
    }

    getApprovalTooltip(): string {
        if (!this.selectedItem?.approvedByUserIds?.length) {
            return this.localizationService.transform('Common.contentLibrary.tooltips.clickToApprove');
        }
        if (this.currentItemId !== this.selectedItem.id) {
            this.currentItemId = this.selectedItem.id;
            this.getApproverName(null);
        }
        const isCurrentUserApproved = this.isApproved();
        const tooltipKey = isCurrentUserApproved ?
            'Common.contentLibrary.tooltips.approvedByYouAndOthers' :
            'Common.contentLibrary.tooltips.approvedByYouAndOthers';
        let tooltip = this.localizationService.transform(tooltipKey, {
            approvers: this.approverNames.join(', ')
        });
        if (!isCurrentUserApproved) {
            tooltip += '\n' + this.localizationService.transform('Common.contentLibrary.tooltips.clickToApprove');
        }
        return tooltip;
    }

    getApproverName(userId: string): void {
        if (this.isLoadingNames) {
            return;
        }
        this.isLoadingNames = true;
        this.approverNames = [];
        this.selectedItem?.approvedByUserIds?.forEach(id => {
            if (this.approverNames.length === 0 && id === this.currentUser?.id) {
                this.approverNames.push(this.localizationService.transform('Common.contentLibrary.single.approval.tooltip'));
                return;
            } else if (id === this.currentUser?.id) {
                this.approverNames.push(this.localizationService.transform('Common.contentLibrary.approval.tooltip'));
                return;
            }
            this.usersService.getUser(Number(id)).subscribe({
                next: (user) => {
                    this.approverNames.push(user.getDisplayName());
                },
                complete: () => {
                    this.isLoadingNames = false;
                }
            });
        });
    }

    isApproved(): boolean {
        return this.selectedItem?.approvedByUserIds?.filter(id => id === this.currentUser?.id).length > 0;
    }

    approveMedia(media: MediaModel): void {
        this.contentLibraryMediaService.approveMedia(media.id).subscribe({
            next: () => {
                if (!this.selectedItem.approvedByUserIds) {
                    this.selectedItem.approvedByUserIds = [];
                }
                if (this.isApproved()) {
                    this.selectedItem.approvedByUserIds =
                        this.selectedItem.approvedByUserIds?.filter(id => id !== this.currentUser?.id);
                } else {
                    this.selectedItem.approvedByUserIds.push(this.currentUser?.id);
                }
                this.currentItemId = null;
                this.getApproverName(null);
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }
}
